import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

export interface PickerOption {
  id: number;
  label: string;
  sublabel?: string;
}

export interface PickerSearchPage {
  items: PickerOption[];
  totalPages?: number;
}

/** Función de búsqueda paginada que el padre provee. */
export type PickerSearchFn = (term: string, page: number) => Observable<PickerSearchPage>;

/**
 * Typeahead estándar del proyecto para seleccionar entidades CRM (contacto, empresa, deal, lead,
 * usuario, producto, tarea padre, etc.). Inspirado en el patrón del lead-form.
 *
 * **Este es el selector buscable estándar: úsalo en lugar de un `<select>` nativo siempre que la
 * lista pueda crecer o el usuario deba buscar para elegir.**
 *
 * Características:
 * - **Enter** dispara búsqueda inmediata aún con el campo vacío (muestra los primeros resultados).
 * - **Scroll infinito** en el dropdown vía `(scroll)` y la `totalPages` que devuelve el padre.
 * - Botón **"+ Nuevo"** opcional que emite `(createNewClicked)` para que el padre abra un drawer.
 * - Badge **Auto-completado** controlado por el padre vía `[autoFilledFlash]`.
 * - Modo `single`: chip readonly con ✕ cuando hay valor.
 * - Modo `multi`: input siempre visible; emite `itemAdded`. Padre maneja chips.
 *
 * Ejemplo:
 * ```html
 * <app-entity-picker
 *   label="Negocio" placeholder="Buscar negocio... (Enter para ver todos)"
 *   [searchFn]="searchDeals" (selectionChange)="onDealChange($event)" />
 * ```
 */
@Component({
  selector: 'app-entity-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './entity-picker.component.html',
})
export class EntityPickerComponent implements OnInit, OnDestroy {
  @Input() mode: 'single' | 'multi' = 'single';
  @Input() label = '';
  @Input() placeholder = 'Buscar... (Enter para ver todos)';
  @Input() helperText?: string;
  @Input() searchFn!: PickerSearchFn;

  /** Single-mode current value */
  @Input() selectedId: number | null = null;
  @Input() selectedLabel: string | null = null;
  @Input() selectedSublabel: string | null = null;

  /** Si true, el chip seleccionado no es removible (prefill obligatorio del contexto). */
  @Input() readonlySelection = false;

  /** Si true, muestra el botón "+ Nuevo" al lado del label. */
  @Input() canCreateNew = false;
  @Input() createNewLabel = 'Nuevo';

  /** Bandera visual: cuando cambia a true, hace flash verde por 2s. */
  @Input() set autoFilledFlash(v: boolean) {
    if (v) {
      this.autoFilled = true;
      setTimeout(() => { this.autoFilled = false; }, 2000);
    }
  }

  @Output() selectionChange = new EventEmitter<PickerOption | null>();
  @Output() itemAdded = new EventEmitter<PickerOption>();
  @Output() createNewClicked = new EventEmitter<void>();

  searchTerm = '';
  results: PickerOption[] = [];
  loading = false;
  loadingMore = false;
  open = false;
  autoFilled = false;

  private currentPage = 0;
  private totalPages = 0;
  private currentTerm = '';

  private readonly term$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  @ViewChild('container') container?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    this.term$
      .pipe(
        debounceTime(280),
        distinctUntilChanged(),
        switchMap((term) => {
          this.currentTerm = term;
          this.currentPage = 0;
          this.results = [];
          this.totalPages = 0;
          this.loading = true;
          return this.searchFn(term, 0).pipe(catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(page => {
        this.results = page.items || [];
        this.totalPages = page.totalPages ?? 1;
        this.loading = false;
        this.open = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInput(): void {
    if (this.searchTerm.trim().length === 0) {
      this.results = [];
      this.open = false;
      return;
    }
    this.term$.next(this.searchTerm);
  }

  /** Enter dispara una búsqueda inmediata sin debounce, incluso con el campo vacío. */
  onEnter(ev: Event): void {
    ev.preventDefault();
    this.currentTerm = this.searchTerm.trim();
    this.currentPage = 0;
    this.results = [];
    this.totalPages = 0;
    this.loading = true;
    this.open = true;

    this.searchFn(this.currentTerm, 0)
      .pipe(catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)), takeUntil(this.destroy$))
      .subscribe(page => {
        this.results = page.items || [];
        this.totalPages = page.totalPages ?? 1;
        this.loading = false;
      });
  }

  onFocus(): void {
    if (this.results.length > 0) this.open = true;
  }

  onScroll(ev: Event): void {
    const el = ev.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMore) return;
    if (this.currentPage + 1 >= this.totalPages) return;

    this.loadingMore = true;
    this.currentPage++;

    this.searchFn(this.currentTerm, this.currentPage)
      .pipe(catchError(() => of({ items: [], totalPages: this.totalPages } as PickerSearchPage)), takeUntil(this.destroy$))
      .subscribe(page => {
        this.results = [...this.results, ...(page.items || [])];
        this.totalPages = page.totalPages ?? this.totalPages;
        this.loadingMore = false;
      });
  }

  pick(opt: PickerOption): void {
    this.open = false;
    if (this.mode === 'single') {
      this.selectedId = opt.id;
      this.selectedLabel = opt.label;
      this.selectedSublabel = opt.sublabel ?? null;
      this.searchTerm = '';
      this.results = [];
      this.selectionChange.emit(opt);
    } else {
      this.searchTerm = '';
      this.results = [];
      this.itemAdded.emit(opt);
    }
  }

  clear(): void {
    if (this.readonlySelection) return;
    this.selectedId = null;
    this.selectedLabel = null;
    this.selectedSublabel = null;
    this.searchTerm = '';
    this.results = [];
    this.selectionChange.emit(null);
  }

  onCreateNew(): void {
    this.createNewClicked.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent): void {
    if (!this.container) return;
    if (!this.container.nativeElement.contains(ev.target as Node)) {
      this.open = false;
    }
  }
}
